import { TableState } from '@tanstack/react-table';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { TableViewFilters } from '../types/table-view-filters';
import { DEFAULT_MIN_PAGE, createQueryParamStorage } from '../utils';

type SupportedState = Pick<
  TableState,
  | 'columnFilters'
  | 'columnOrder'
  | 'columnPinning'
  | 'pagination'
  | 'sorting'
  | 'rowSelection'
> & {
  viewFilters: TableViewFilters;
};

type StateUpdater<T> = T | ((prev: T) => T);

type Setters<State> = {
  [K in keyof State as `set${Capitalize<string & K>}`]: (
    value: StateUpdater<State[K]>
  ) => void;
};

type SupportedStore = SupportedState &
  Setters<SupportedState> & {
    initialized?: boolean;
    initialize?: (state: Partial<SupportedState>) => void;
  };

interface TableStateStore extends SupportedStore {}

const defaultState: SupportedState = {
  columnFilters: [],
  columnOrder: [],
  columnPinning: { left: [], right: [] },
  pagination: { pageIndex: DEFAULT_MIN_PAGE, pageSize: 10 },
  sorting: [],
  rowSelection: {},
  viewFilters: {},
};

const createTableSetters = <T extends {}>(
  state: T,
  set: (value: Partial<T>) => void,
  getState: () => T
) => {
  const keys: (keyof T)[] = Object.keys(state) as (keyof T)[];

  const setters = keys.reduce<Setters<T>>((acc, key) => {
    const setterName = `set${String(key).charAt(0).toUpperCase()}${String(
      key
    ).slice(1)}` as keyof Setters<T>;

    return {
      ...acc,
      [setterName]: (value: StateUpdater<T>) => {
        const newValue =
          typeof value === 'function'
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
              (value as Function)(getState()[key] as T[keyof T])
            : value;

        set({ [key]: newValue } as Partial<T>);
      },
    };
  }, {} as Setters<T>);

  return setters;
};

const tableStores = new Map<string, UseBoundStore<StoreApi<TableStateStore>>>();

const createTableStore = (
  tableName: string
): UseBoundStore<StoreApi<TableStateStore>> =>
  create<TableStateStore>()(
    persist(
      (set, getState) => ({
        initialized: false,
        initialize: (state: Partial<SupportedState>) => {
          set({
            ...state,
            initialized: true,
          });
        },
        ...defaultState,
        ...createTableSetters<SupportedState>(defaultState, set, getState),
      }),
      {
        name: tableName,
        storage: createJSONStorage(() => createQueryParamStorage(tableName)),
      }
    )
  );

export const getTableStore = (
  tableName: string
): UseBoundStore<StoreApi<TableStateStore>> => {
  const existingStore = tableStores.get(tableName);

  if (existingStore) return existingStore;

  const newStore = createTableStore(tableName);
  tableStores.set(tableName, newStore);

  return newStore;
};
